import React, { useState } from 'react'
import { Routes, Route, useNavigate } from 'react-router-dom'
import logo from '../../assets/logo.svg'
import './App.css'
import Scenario from './Scenario.tsx'
import List from './List.tsx'
import ScenarioItemsPage from './ScenarioItemsPage.tsx'
import ListBuilder from './ListBuilder.tsx'
import Flows from './Flows.tsx'
import FlowEditor from './FlowEditor.tsx'
import { AcceptInvite } from './AcceptInvite.tsx'
import { About } from './About.tsx'
import { Privacy } from './Privacy.tsx'
import { Help } from './Help.tsx'
import { Feedback } from './Feedback.tsx'
import { SyncNotification } from '../components/SyncNotification'
import { SyncStatusIndicator } from '../components/SyncStatusIndicator'
import { UserAvatar } from '../components/UserAvatar'
import { CreateScenarioModal } from '../components/CreateScenarioModal'
import { StructuredData } from '../components/StructuredData'
import { ToastCenter } from '../components/ToastCenter'
import { useScenarios } from '../hooks/useScenarios'
import { useAuth } from '../hooks/useAuth'
import { useCommand } from '../hooks/useCommand'
import { ScenarioKindIcon } from '../components/PageKindIcons'
import { ThemeMenu } from '../components/ThemeMenu'
import { usePwaUpdate } from '../hooks/usePwaUpdate'
import { PwaUpdateBanner } from '../components/PwaUpdateBanner'

function ScenariosList() {
  const navigate = useNavigate()
  const { user, authChecked, userDetails, userDetailsLoading, login, logout } = useAuth()
  const command = useCommand()
  const [showUserDetails, setShowUserDetails] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newScenarioName, setNewScenarioName] = useState('')
  const [newScenarioDescription, setNewScenarioDescription] = useState('')
  

  // Use hook for scenarios
  const { scenarios, loading: scenariosLoading, syncing: scenariosSyncing } = useScenarios()

  const createScenario = async () => {
    if (!newScenarioName.trim()) return

    try {
      const scenarioId = crypto.randomUUID()

      await command.createScenario(
        scenarioId,
        newScenarioName.trim(),
        newScenarioDescription.trim() || ''
      )
      
      setNewScenarioName('')
      setNewScenarioDescription('')
      setShowCreateModal(false)
      // Navigate to the newly created scenario
      navigate(`/scenario/${scenarioId}`)
    } catch (error) {
      console.error('Error creating scenario:', error)
    }
  }

  const handleScenarioClick = (scenarioId: string) => {
    navigate(`/scenario/${scenarioId}`)
  }

  if (!authChecked) {
    return <div>Loading...</div>
  }

  return (
    <div className="app-container">
      {!user && (
        <>
          <StructuredData type="WebApplication" />
          <StructuredData type="Organization" />
        </>
      )}
      {user && <SyncNotification />}
      <header className="app-header">
        <div className="header-content">
          <img src={logo} alt="Ordelio" className="app-logo" />
          <div className="header-right">
            {user && <SyncStatusIndicator />}
            <ThemeMenu />
            <div className="user-section">
            {user ? (
              <div className="user-info">
                <UserAvatar
                  name={userDetails?.name}
                  email={userDetails?.email}
                  displayName={user.userDetails}
                  photoUrl={userDetails?.photoUrl}
                  loading={userDetailsLoading}
                  onClick={() => setShowUserDetails(!showUserDetails)}
                />
                {showUserDetails && (
                  <div className="user-details">
                    <div className="user-details-header">
                      {userDetails?.name && <span className="user-details-name">{userDetails.name}</span>}
                      <span className="user-details-email">{userDetails?.email || user.userDetails || 'Loading...'}</span>
                    </div>
                    <div className="user-details-divider" />
                    <button
                      onClick={() => {
                        setShowUserDetails(false)
                        navigate('/feedback')
                      }}
                      className="user-details-item"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1Zm0 10.5a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM8.75 7.25a.75.75 0 0 1-1.5 0v-3a.75.75 0 0 1 1.5 0v3Z" fill="currentColor"/></svg>
                      Send Feedback
                    </button>
                    <button onClick={logout} className="user-details-item user-details-item--danger">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 3.5A1.5 1.5 0 0 1 3.5 2h4a.5.5 0 0 1 0 1h-4a.5.5 0 0 0-.5.5v9a.5.5 0 0 0 .5.5h4a.5.5 0 0 1 0 1h-4A1.5 1.5 0 0 1 2 12.5v-9Z" fill="currentColor"/><path d="M10.646 4.646a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708-.708L12.793 8.5H6.5a.5.5 0 0 1 0-1h6.293l-2.147-2.146a.5.5 0 0 1 0-.708Z" fill="currentColor"/></svg>
                      Log Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button onClick={login} className="login-btn">Login with Google</button>
            )}
            </div>
          </div>
        </div>
      </header>

      <PwaUpdateBanner />

      <main className="app-main">
        {user ? (
          <div className="scenarios-section">
            <div className="scenarios-header">
              <h2>Your Scenarios</h2>
              {scenarios.length > 0 && (
                <button onClick={() => setShowCreateModal(true)} className="create-btn header-action">
                  + New Scenario
                </button>
              )}
            </div>
            
            <div className="scenarios-grid">
              {(scenariosLoading || scenariosSyncing) && scenarios.length === 0 ? (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <p>{scenariosSyncing ? 'Syncing your scenarios...' : 'Loading scenarios...'}</p>
                </div>
              ) : scenarios.length > 0 ? (
                scenarios.map((scenario, index) => (
                  <div key={scenario.id || index} className="scenario-tile">
                    <div 
                      className="scenario-content clickable" 
                      onClick={() => handleScenarioClick(scenario.id)}
                    >
                      <div className="scenario-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0 }}>
                          <ScenarioKindIcon style={{ minWidth: 20, width: 20, height: 20, opacity: 0.7 }} />
                          <h3>{scenario.name}</h3>
                        </div>
                        <span className={`role-badge ${scenario.role === 'owner' ? 'owner' : 'collaborator'}`}>
                          {scenario.role}
                        </span>
                      </div>
                      {scenario.description && <p>{scenario.description}</p>}
                    </div>
                  </div>
                ))
              ) : (
                <div key="create-new" className="scenario-tile create-tile clickable" onClick={() => setShowCreateModal(true)}>
                  <div className="create-content">
                    <div className="plus-icon">+</div>
                    <h3>Create New Scenario</h3>
                    <p style={{ textAlign: 'center', opacity: 0.7 }}>Get started by creating your first scenario</p>
                  </div>
                </div>
              )}
            </div>

            <CreateScenarioModal
              isOpen={showCreateModal}
              onClose={() => setShowCreateModal(false)}
              onCreate={createScenario}
              name={newScenarioName}
              description={newScenarioDescription}
              onNameChange={setNewScenarioName}
              onDescriptionChange={setNewScenarioDescription}
            />
          </div>
        ) : (
          <>
            {/* Hero Section */}
            <section className="hero-section">
              <div className="welcome-content">
                <img src={logo} alt="Ordelio Logo - Smart Shopping List App" className="welcome-logo" />
                <h1 className="welcome-title">Routine-Friendly Lists for Shopping & Travel</h1>
                <p className="welcome-description">
                  Ordelio helps you build repeatable checklists for groceries, packing, and recurring errands.
                  Organize items in the order you like, follow the same flow every time, and stay on track even offline.
                  Share and sync when you’re online.
                </p>
                
                <button onClick={login} className="cta-button">
                  Get Started with Google
                </button>
                
                <p className="trust-badge">✓ Free to use • ✓ Works offline • ✓ Syncs everywhere</p>
              </div>
            </section>

            {/* Key Features Section */}
            <section id="features" className="features-section">
              <h2 className="section-title">Everything You Need for Effortless Repeatable Lists</h2>
              <div className="features-grid">
                <div className="feature-card">
                  <div className="feature-icon">📱</div>
                  <h3>Works Offline</h3>
                  <p>Never worry about connectivity. Add items, check off tasks, and organize lists even in areas with poor signal. Everything syncs automatically when you're back online.</p>
                </div>
                <div className="feature-card">
                  <div className="feature-icon">🤝</div>
                  <h3>Share Lists Online</h3>
                  <p>When connected to the internet, share shopping lists with family and friends. Everyone sees updates when items are added or purchased—no more duplicate buying.</p>
                </div>
                <div className="feature-card">
                  <div className="feature-icon">💡</div>
                  <h3>Item History</h3>
                  <p>Ordelio remembers items you've added before and shows them as suggestions, making it faster to rebuild your shopping lists.</p>
                </div>
                <div className="feature-card">
                  <div className="feature-icon">🔄</div>
                  <h3>Multi-Device Sync</h3>
                  <p>Access your lists from phone, tablet, or computer. Changes appear instantly across all devices and even across multiple browser tabs.</p>
                </div>
                <div className="feature-card">
                  <div className="feature-icon">🗺️</div>
                  <h3>Custom Store Routes</h3>
                  <p>Organize items to match your store’s layout. Walk your usual route and check items off as you go—no more backtracking through aisles.</p>
                </div>
                <div className="feature-card">
                  <div className="feature-icon">🔁</div>
                  <h3>Repeatable Routines</h3>
                  <p>Build a list once and reuse it. Create scenarios for different stores, trips, or routines, and quickly rebuild lists from what worked last time.</p>
                </div>
              </div>
            </section>

            {/* How It Works Section */}
            <section id="how-it-works" className="how-it-works-section">
              <h2 className="section-title">How Ordelio Works</h2>
              <div className="steps-container">
                <div className="step">
                  <div className="step-number">1</div>
                  <h3>Sign In with Google</h3>
                  <p>Get started in seconds with your Google account. Your lists sync across all devices.</p>
                </div>
                <div className="step">
                  <div className="step-number">2</div>
                  <h3>Create Your First Scenario</h3>
                  <p>Set up a scenario for a repeatable routine—weekly groceries, travel packing, a pharmacy run, or any list you want to reuse.</p>
                </div>
                <div className="step">
                  <div className="step-number">3</div>
                  <h3>Build Your Shopping List</h3>
                  <p>Add items with smart suggestions based on your history. Share with family when you want to collaborate.</p>
                </div>
                <div className="step">
                  <div className="step-number">4</div>
                  <h3>Shop Efficiently</h3>
                  <p>Follow your list in the order you prefer, check things off as you go, and stay focused from start to finish.</p>
                </div>
              </div>
            </section>

            {/* Use Cases Section */}
            <section id="use-cases" className="use-cases-section">
              <h2 className="section-title">Great for Repeatable Routines</h2>
              <div className="use-cases-grid">
                <div className="use-case-card">
                  <h3>🛒 Weekly Grocery Shopping</h3>
                  <p>Create recurring grocery lists with smart suggestions. Organize items by store layout and never forget essentials.</p>
                </div>
                <div className="use-case-card">
                  <h3>👨‍👩‍👧‍👦 Family Collaboration</h3>
                  <p>Share lists with family members. Everyone can add items throughout the week, and see real-time updates while shopping.</p>
                </div>
                <div className="use-case-card">
                  <h3>🧳 Travel Packing & Checklists</h3>
                  <p>Keep a packing list you can reuse for every trip. Check items off as you go and refine it over time.</p>
                </div>
                <div className="use-case-card">
                  <h3>🏪 Multiple Stores & Errands</h3>
                  <p>Create separate scenarios for different stores or errand runs—each with the right items and your preferred order.</p>
                </div>
              </div>
            </section>

            {/* CTA Section */}
            <section className="cta-section">
              <h2>Ready for calmer, repeatable trips?</h2>
              <p>Start with your usual list, then reuse and refine it whenever you need it.</p>
              <button onClick={login} className="cta-button">
                Get Started with Google
              </button>
            </section>

            <footer className="footer">
              <div className="footer-content">
                <div className="footer-section">
                  <h4>Ordelio</h4>
                  <p>Routine-friendly lists that work offline</p>
                </div>
                <div className="footer-section">
                  <h4>Product</h4>
                  <ul>
                    <li><a href="/#features">Features</a></li>
                    <li><a href="/#how-it-works">How It Works</a></li>
                    <li><a href="/#use-cases">Use Cases</a></li>
                  </ul>
                </div>
                <div className="footer-section">
                  <h4>Company</h4>
                  <ul>
                    <li><a href="/about">About</a></li>
                    <li><a href="/privacy">Privacy Policy</a></li>
                    <li><a href="/help">Help & Documentation</a></li>
                  </ul>
                </div>
                <div className="footer-section">
                  <h4>Connect</h4>
                  <p className="footer-copyright">© 2026 Ordelio. All rights reserved.</p>
                </div>
              </div>
            </footer>
          </>
        )}
      </main>
    </div>
  )
}

import { ScenarioLayout } from '../layouts/ScenarioLayout'
import { useLocation } from 'react-router-dom'

function App() {
  const { needsRefresh, applyUpdate } = usePwaUpdate()
  const location = useLocation()

  // Auto-apply pending update on navigation (the user just finished an action)
  const prevPathRef = React.useRef(location.pathname)
  React.useEffect(() => {
    if (location.pathname !== prevPathRef.current && needsRefresh) {
      applyUpdate()
    }
    prevPathRef.current = location.pathname
  }, [location.pathname, needsRefresh, applyUpdate])

  return (
    <>
      <ToastCenter />
      <Routes>
        <Route path="/" element={<ScenariosList />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/about" element={<About />} />
        <Route path="/feedback" element={<Feedback />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/help" element={<Help />} />
        
        {/* Scenario Scope - Single Subscription for all nested routes */}
        <Route path="/scenario/:scenarioId" element={<ScenarioLayout />}>
          <Route index element={<Scenario />} />
          <Route path="items" element={<ScenarioItemsPage />} />
          <Route path="flows" element={<Flows />} />
          <Route path="flow/:flowId" element={<FlowEditor />} />
          <Route path="list-builder" element={<ListBuilder />} />
          <Route path="list/:listId/add-items" element={<ListBuilder />} />
          <Route path="list/:listId" element={<List />} />
        </Route>
      </Routes>
    </>
  )
}

export default App
